import { Plugins } from "@capacitor/core";
import {
  showPersistentToast,
  showAutoToast,
  hidePersistentToast,
} from "./toast";

const { Network } = Plugins;

let wasOffline = false;
let isCheckingSpeed = false;

/**
 * Test network speed by fetching a small resource
 */
async function checkNetworkSpeed() {
  if (isCheckingSpeed) return 'normal';

  isCheckingSpeed = true;
  const startTime = Date.now();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    // Use a simple, reliable endpoint (you can replace with your own API)
    await fetch('https://www.google.com/favicon.ico', {
      method: 'HEAD',
      cache: 'no-cache',
      signal: controller.signal,
      mode: 'no-cors'
    });

    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    isCheckingSpeed = false;

    if (duration > 2000) {
      return 'slow';
    }

    return 'normal';
  } catch (error) {
    isCheckingSpeed = false;

    if (error.name === 'AbortError') {
      return 'slow';
    }

    return 'offline';
  }
}

export { checkNetworkSpeed };

/**
 * Check if device has internet connection
 */
export async function canCallApi() {
  try {
    const status = await Network.getStatus();
    return status.connected;
  } catch (error) {
    console.error('Error checking network status:', error);
    // Fallback to browser's navigator.onLine if available
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }
}

/**
 * Get current network status
 */
export async function getNetworkStatus() {
  try {
    return await Network.getStatus();
  } catch (error) {
    console.error('Error getting network status:', error);
    return { connected: navigator.onLine };
  }
}

/**
 * Initialize network listener - works on both web and native
 */
export function initNetworkListener() {
  console.log('🔧 ===== INITIALIZING NETWORK LISTENER =====');

  // Check if running in browser (web) or native app
  const isWeb = !window.Capacitor || window.Capacitor.platform === 'web';
  console.log('📱 Platform:', isWeb ? 'web' : 'native');

  // For web (browser), use navigator.onLine and events
  if (isWeb) {
    console.log('🌐 Using browser network detection');

    // Check initial status
    const isOnline = navigator.onLine;
    console.log('📡 Initial browser status:', isOnline ? 'ONLINE' : 'OFFLINE');

    if (!isOnline) {
      console.log('❌ BROWSER STARTED OFFLINE - SHOWING TOAST');
      wasOffline = true;
      showAutoToast(
        "Internet is not available. Please turn on internet.",
        "error-toast",
        3000
      );
    }

    // Listen for online/offline events
    window.addEventListener('online', async () => {
      console.log('✅ ===== BROWSER CAME ONLINE =====');
      await hidePersistentToast();

      if (wasOffline) {
        wasOffline = false;
        const speed = await checkNetworkSpeed();
        console.log('📊 Speed result:', speed);

        if (speed === 'slow') {
          await showAutoToast("Slow internet connection", "warning-toast", 3000);
        } else {
          await showAutoToast("Back online", "success-toast", 2500);
        }
      }
    });

    window.addEventListener('offline', async () => {
      console.log('❌ ===== BROWSER WENT OFFLINE =====');
      wasOffline = true;
      await showAutoToast(
        "Internet is not available. Please turn on internet.",
        "error-toast",
        3000
      );
    });

    console.log('✅ Browser network listeners setup complete');
    return;
  }

  // For native (iOS/Android), use Capacitor Network plugin
  console.log('📱 Using Capacitor Network plugin');

  // Check initial status
  Network.getStatus().then((status) => {
    console.log('📡 Initial network status:', JSON.stringify(status));

    if (!status.connected) {
      console.log('❌ APP STARTED OFFLINE - SHOWING TOAST');
      wasOffline = true;
      showAutoToast(
        "Internet is not available. Please turn on internet.",
        "error-toast",
        3000
      );
    } else {
      console.log('✅ APP STARTED ONLINE');
      // Check speed after a delay
      setTimeout(async () => {
        const speed = await checkNetworkSpeed();
        if (speed === 'slow') {
          await showAutoToast("Slow internet connection", "warning-toast", 3000);
        }
      }, 2000);
    }
  }).catch((error) => {
    console.error('❌ Failed to get initial network status:', error);
  });

  // Set up listener for changes
  Network.addListener("networkStatusChange", async (status) => {
    console.log('🔄 ===== NETWORK STATUS CHANGED =====');
    console.log('📡 New status:', JSON.stringify(status));

    if (!status.connected) {
      console.log('❌ NETWORK WENT OFFLINE - SHOWING TOAST');
      wasOffline = true;
      await showAutoToast(
        "Internet is not available. Please turn on internet.",
        "error-toast"
      );
    } else {
      console.log('✅ NETWORK CAME ONLINE');
      await hidePersistentToast();

      if (wasOffline) {
        wasOffline = false;
        const speed = await checkNetworkSpeed();
        console.log('📊 Speed result:', speed);

        if (speed === 'slow') {
          await showPersistentToast("Slow internet connection", "warning-toast");
        } else {
          await showAutoToast("Back online", "success-toast", 2500);
        }
      } else {
        // Check speed even if wasn't offline before
        const speed = await checkNetworkSpeed();
        if (speed === 'slow') {
          await showAutoToast("Slow internet connection", "warning-toast", 3000);
        } else {
          await hidePersistentToast();
        }
      }
    }
  });

  console.log('✅ Capacitor network listener setup complete');
}

/**
 * Add custom network listener (callback style)
 */
export function addNetworkListener(callback) {
  const isWeb = !window.Capacitor || window.Capacitor.platform === 'web';

  if (isWeb) {
    window.addEventListener('online', () => callback({ connected: true }));
    window.addEventListener('offline', () => callback({ connected: false }));
  } else {
    Network.addListener('networkStatusChange', callback);
  }
}